import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faUser } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import GroupConfig from "@serie3/common/Modules/Groups/Resources/Endpoints";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import {
  PERSONAL_SOURCE,
  groupSourceKey,
  collaborationGroups,
} from "@serie3/mymediaplanner/Modules/Documents/Domain/fileSources";
import ToastMessages from "../../../../ToastMessages";

/**
 * The locations the file manager can show. A source is
 *   { key, label, isGroup, group?, endpoints, normalizeFiles, canDelete }
 * where `endpoints` has the same keys as DocumentsEndpoints (index / new /
 * delete / show / moveToFolder / folders / folderRename / folderDelete, and
 * imagePosts for the caller's own files).
 */

/** The group's file routes with the group id already in the paths. */
export const groupFileEndpoints = (groupId) => {
  const d = MediaplannerGroupEndpoints.endpoints.documents;
  return {
    index: d.list + groupId,
    new: d.new + groupId,
    delete: d.delete + groupId + "/",
    show: d.show + groupId + "/",
    moveToFolder: d.moveToFolder + groupId + "/",
    folders: d.folders + groupId,
    folderRename: d.folderRename + groupId + "/rename",
    folderDelete: d.folders + groupId + "?path=",
  };
};

/** group list rows are { document, uploader, can_delete }; the file manager wants flat documents */
export const flattenRows = (data) =>
  (data?.documents ?? []).map((row) =>
    row?.document ? { ...row.document, uploader: row.uploader, can_delete: row.can_delete } : row
  );

export const personalSource = (label) => ({
  key: PERSONAL_SOURCE,
  label,
  isGroup: false,
  endpoints: DocumentsEndpoints,
  normalizeFiles: (data) => data?.documents ?? [],
  canDelete: () => true,
});

/** Every member sees and uploads; the API says per row whether the caller may delete. */
export const groupSource = (group) => ({
  key: groupSourceKey(group.id),
  label: group.name,
  isGroup: true,
  group,
  endpoints: groupFileEndpoints(group.id),
  normalizeFiles: flattenRows,
  canDelete: (file) => file.can_delete !== false,
});

/** One source per collaboration group the caller belongs to; empty when the list cannot be read. */
export const loadGroupSources = async () => {
  const response = await crudApi.justGet(GroupConfig.endpoints.list);
  if (!response.ok) return [];
  return collaborationGroups(response.data).map(groupSource);
};

/**
 * A copy of a group file in the caller's own file manager. The post editor
 * only attaches files the poster owns, so this is how a group asset gets into
 * a post. Resolves with the crudApi response; 402 = own quota used up.
 */
export const copyGroupFile = (source, file) =>
  crudApi.justGet(MediaplannerGroupEndpoints.endpoints.documents.copy + source.group.id + "/" + file.id);

/** Shows who uploaded a group file and, unless hidden, a "copy to my files" button. */
export const GroupFileActions = ({ source, file, showCopy = true, onCopied = null }) => {
  const { t } = useTranslation();
  const [copying, setCopying] = useState(false);

  const copy = async () => {
    setCopying(true);
    const response = await copyGroupFile(source, file);
    setCopying(false);
    if (response.ok) {
      ToastMessages.SuccessfulSave(t("Backend.Groups.Files.Copied", { defaultValue: "Copied to your files" }));
      if (typeof onCopied === "function") onCopied(response.data);
    } else if (response.status === 402) {
      ToastMessages.Error(t("Backend.Groups.Files.QuotaExceeded", { defaultValue: "Your file quota is used up" }));
    } else {
      ToastMessages.Error();
    }
  };

  return (
    <>
      {file.uploader?.name && (
        <span
          title={file.uploader.username ?? ""}
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
            borderRadius: 8, background: "#f8f9fa", fontSize: 12, color: "#6b7280",
            maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          <FontAwesomeIcon icon={faUser} style={{ fontSize: 10, flexShrink: 0 }} />
          {file.uploader.name}
        </span>
      )}
      {showCopy && (
        <button
          type="button"
          disabled={copying}
          onClick={(e) => { e.stopPropagation(); copy(); }}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e5ea",
            background: "#fff", fontSize: 12, fontWeight: 600, color: "#374151",
            cursor: copying ? "default" : "pointer",
            opacity: copying ? 0.6 : 1,
            transition: "border-color 0.15s ease",
          }}
          onMouseEnter={(e) => { if (!copying) e.currentTarget.style.borderColor = "#51d28c"; }}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e5ea")}
        >
          <FontAwesomeIcon icon={faCopy} style={{ color: "#51d28c" }} />
          {t("Backend.Groups.Files.CopyToMine", { defaultValue: "Copy to my files" })}
        </button>
      )}
    </>
  );
};
